// Chat's AI-specific message rendering, expressed as renderer-registry plugins
// for @workspace/messaging-ui (tdd-0050). Everything that used to be hard-coded
// in message.tsx — <think> blocks, tool calls, Brain recalled/saved bubbles —
// lives here now, injected through the registry. The package stays AI-agnostic;
// apps/messaging will register a different set (reactions, system notices, …).

import type {
  ChatMessage,
  MessageRenderers,
  SegmentRenderer,
  ExtrasRenderer,
} from "@workspace/messaging-ui";
import { StreamingMarkdown } from "@workspace/ui-text/streaming-markdown";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../ui";
import { AlertCircle, Brain, RotateCcw } from "lucide-react";
import { RecalledMemories, MemorySaved } from "@workspace/ui/components/memory";
import { cn } from "../../utils";
import { ToolCalls } from "./tool-call";
import { LocationConsent } from "./location-consent";
import type { GeolocationStatus } from "../../hooks/use-geolocation";
import { splitComponentBlocks } from "../../utils/component-blocks";
import { ComponentSegment } from "./component-segment";
import type { Message as ChatAppMessage, ToolCallWithResult } from "../../types";

/** Payload carried on a chat message's `extras`, consumed by the extras renderer. */
interface ChatExtras {
  memorySaved?: { fact: string; id: string | null };
  recalledMemories?: string[];
  recalledQuery?: string;
  /** Provided by ChatConversation so the extras renderer can wire undo. */
  onDeleteMemory?: (messageId: string) => void;
  brainBaseUrl?: string;
  /**
   * A location-aware tool call in THIS message is waiting on consent.
   *
   * Rendered inline, under the tool call that caused it, rather than pinned
   * above the composer — it reads as part of the conversation, like every
   * other generative-UI affordance, instead of as chrome.
   */
  locationConsent?: {
    toolName: string;
    reason?: string;
    status: GeolocationStatus;
    error?: string | null;
    onShare: () => void;
    onDecline: () => void;
  };
  /** This user turn got no reply, and can be sent again. */
  failed?: {
    message: string;
    onRetry?: () => void;
  };
}

/** Data for a "thinking" segment (parsed from inline <think> tags). */
interface ThinkingSegmentData {
  defaultExpanded: boolean;
}

/** Data for a "tool-call" segment. */
interface ToolCallSegmentData {
  toolCalls: ToolCallWithResult[];
}

/** Data for a "component" (generative-UI) segment. */
interface ComponentSegmentData {
  /** True while the closing fence hasn't arrived — still streaming. */
  partial: boolean;
}

// ── <think> parsing (chat-specific) ─────────────────────────────────────────

interface TextOrThinking {
  type: "text" | "thinking";
  content: string;
}

function parseThinkingContent(content: string): TextOrThinking[] {
  const segments: TextOrThinking[] = [];
  const thinkRegex = /<think>([\s\S]*?)<\/think>/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = thinkRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      const textContent = content.slice(lastIndex, match.index).trim();
      if (textContent) segments.push({ type: "text", content: textContent });
    }
    const thinkingContent = match[1].trim();
    if (thinkingContent) segments.push({ type: "thinking", content: thinkingContent });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    const remaining = content.slice(lastIndex).trim();
    if (remaining) segments.push({ type: "text", content: remaining });
  }
  if (segments.length === 0 && content.trim()) {
    segments.push({ type: "text", content: content.trim() });
  }
  return segments;
}

function getModelDisplayName(modelId?: string): string {
  if (!modelId) return "Assistant";
  const lower = modelId.toLowerCase();
  if (lower.includes("claude")) return "Claude";
  if (lower.includes("gemma")) return "Gemma";
  if (lower.includes("qwen")) return "Qwen";
  if (lower.includes("llama")) return "Llama";
  if (lower.includes("mistral")) return "Mistral";
  if (lower.includes("mixtral")) return "Mixtral";
  if (lower.includes("gpt-4")) return "GPT-4";
  if (lower.includes("gpt-3")) return "GPT-3";
  if (lower.includes("phi")) return "Phi";
  if (lower.includes("deepseek")) return "DeepSeek";
  if (lower.includes("codestral")) return "Codestral";
  if (lower.includes("command")) return "Command";
  if (lower.includes("gemini")) return "Gemini";
  const withoutOrg = modelId.replace(/^[^/]+\//, "");
  const firstPart = withoutOrg.split(/[-_./]/)[0];
  if (firstPart && firstPart.length > 1) {
    return firstPart.charAt(0).toUpperCase() + firstPart.slice(1);
  }
  return "Assistant";
}

// ── Map chat's Message → the package's ChatMessage ──────────────────────────

/**
 * Convert an apps/chat `Message` into the transport-agnostic `ChatMessage` the
 * package renders. The AI-specific structure (inline <think> blocks, tool calls,
 * memory affordances) becomes segments + extras so the package can dispatch them
 * through the registry without knowing what they mean.
 */
/**
 * Pull generative-UI specs out of completed tool results.
 *
 * A tool result is plain text as far as the transcript is concerned, but some
 * tools answer with a `json-render` block instead of prose — the elicited
 * settings form in tdd-0059 being the motivating case. Reusing
 * `splitComponentBlocks` means detection is identical to assistant content, so
 * a spec renders the same whether a tool or the model produced it.
 *
 * Only *completed* calls are considered: a spec still streaming in would compile
 * to a broken tree, and the accordion already shows progress for in-flight ones.
 */
/**
 * The `formId` a json-render chunk declares, if any.
 *
 * Used to spot the SAME form arriving twice in one message: once from the tool
 * result and once from the assistant's own content, because the model echoes
 * the tool output back in its reply. Two segments meant two mounted forms, two
 * subscriptions to the action bus, and one click posting the turn twice or
 * three times.
 */
function formIdInChunk(chunk: string): string | undefined {
  const match = chunk.match(/"formId"\s*:\s*"([^"]+)"/);
  return match?.[1];
}

function componentSegmentsFromToolCalls(
  toolCalls: ToolCallWithResult[]
): NonNullable<ChatMessage["segments"]> {
  const segments: NonNullable<ChatMessage["segments"]> = [];

  for (const call of toolCalls) {
    if (call.status !== "completed" || !call.result) continue;

    const text = call.result.content
      .map((part) => part.text ?? "")
      .filter(Boolean)
      .join("\n");
    if (!text.includes("```")) continue;

    for (const chunk of splitComponentBlocks(text)) {
      // Prose around the spec is deliberately dropped: the accordion already
      // shows the raw result, so repeating it here would duplicate the text
      // for every tool that happens to return a form.
      if (chunk.type === "component" && !chunk.partial) {
        segments.push({
          kind: "component",
          text: chunk.content,
          data: { partial: false } satisfies ComponentSegmentData,
        });
      }
    }
  }

  return segments;
}

export function toChatMessage(
  message: ChatAppMessage,
  opts: {
    modelId?: string;
    thinkingDisplayMode: "collapsed" | "expanded";
    onDeleteMemory?: (messageId: string) => void;
    brainBaseUrl?: string;
    /**
     * The pending location request, plus WHICH message triggered it, so the
     * chip renders under that tool call rather than on every message.
     */
    locationConsent?: {
      messageId: string;
      props: NonNullable<ChatExtras["locationConsent"]>;
    };
    onIncludeFullFile?: (attachment: { name: string; path?: string }) => void;
    /** Remove a durably-stored artifact (tdd-0076). */
    onDeleteArtifact?: (attachment: { messageId: string; attachmentId: string; storageKey: string }) => void;
    /** Storage keys with a delete currently in flight. */
    deletingArtifacts?: ReadonlySet<string>;
    /** User asked to start a fresh edit from the original (tdd-0076). */
    onRevertToOriginal?: (artifact: { rootKey: string; name: string }) => void;
    /** Re-send a user turn that never got a reply. */
    onRetry?: (messageId: string) => void;
    /**
     * Form ids already rendered EARLIER IN THE CONVERSATION.
     *
     * Deduping within a message is not enough: the same form arrives in two
     * turns — the assistant message carrying the tool result, and the reply
     * that echoes that output back. Each mounted its own form, each subscribed
     * to the action bus, and one click posted the turn twice.
     *
     * Shared across the map so the FIRST occurrence wins and later ones are
     * dropped. Mutated by design — the caller owns the set for one render pass.
     */
    renderedFormIds?: Set<string>;
  }
): ChatMessage {
  const isUser = message.role === "user";
  const effectiveModelId = message.modelId || opts.modelId;

  const segments: ChatMessage["segments"] = [];
  // Conversation-wide when the caller supplies one, so a form echoed into a
  // later turn does not mount a second time; falls back to per-message.
  const seenFormIds = opts.renderedFormIds ?? new Set<string>();

  // Tool calls render as a leading segment (matches the original order:
  // attachments, then tool calls, then content).
  if (message.toolCalls && message.toolCalls.length > 0) {
    segments.push({
      kind: "tool-call",
      data: { toolCalls: message.toolCalls } satisfies ToolCallSegmentData,
    });

    // A tool may return generative UI rather than prose — an elicited settings
    // form is the motivating case (tdd-0059): drawthings' `generate_image` with
    // `tune: true` returns a json-render spec for the user to fill in.
    //
    // Without this the spec lands inside the tool-call accordion as raw JSON and
    // is never compiled, so the user sees nothing to fill in. The model then
    // narrates the fields conversationally instead, because from its side the
    // form *was* returned. Rendering here rather than inside ToolCallSegment
    // keeps all spec compilation in one place, and means ANY MCP tool that
    // returns a spec renders — not just drawthings.
    for (const seg of componentSegmentsFromToolCalls(message.toolCalls)) {
      const id = seg.kind === "component" ? formIdInChunk(seg.text ?? "") : undefined;
      if (id) seenFormIds.add(id);
      segments.push(seg);
    }
  }

  // Assistant content is parsed for <think> blocks; user content is a single text.
  if (!isUser && message.content) {
    for (const seg of parseThinkingContent(message.content)) {
      if (seg.type === "thinking") {
        segments.push({
          kind: "thinking",
          text: seg.content,
          data: {
            defaultExpanded: opts.thinkingDisplayMode === "expanded",
          } satisfies ThinkingSegmentData,
        });
      } else {
        // Non-thinking prose may itself carry generative-UI blocks, so it is
        // split again here (tdd-0059). Splitting AFTER <think> extraction
        // means a fence inside a thinking block stays inside it — reasoning
        // about a form is not the same as rendering one.
        for (const chunk of splitComponentBlocks(seg.content)) {
          if (chunk.type === "component") {
            // Already rendered from the tool result — the model echoing its own
            // tool output must not mount a second copy of the same form.
            const dupId = formIdInChunk(chunk.content);
            if (dupId && seenFormIds.has(dupId)) continue;
            if (dupId) seenFormIds.add(dupId);
            segments.push({
              kind: "component",
              text: chunk.content,
              data: { partial: chunk.partial } satisfies ComponentSegmentData,
            });
          } else {
            segments.push({ kind: "text", text: chunk.content });
          }
        }
      }
    }
  } else if (message.content) {
    segments.push({ kind: "text", text: message.content });
  }

  const extras: ChatExtras = {
    // Only on the message whose tool call is waiting — `pendingLocation`
    // carries the id so the chip lands under the call, not on every message.
    locationConsent:
      opts.locationConsent?.messageId === message.id
        ? opts.locationConsent.props
        : undefined,
    memorySaved: message.memorySaved,
    recalledMemories: message.recalledMemories,
    recalledQuery: message.recalledQuery,
    onDeleteMemory: opts.onDeleteMemory,
    brainBaseUrl: opts.brainBaseUrl,
    // A turn that never got a reply. The retry is bound to THIS message's id so
    // the conversation rewinds to exactly this point when it is pressed.
    failed: message.failed
      ? {
          message: message.failed.message,
          onRetry: opts.onRetry
            ? () => opts.onRetry?.(message.id)
            : undefined,
        }
      : undefined,
  };

  return {
    id: message.id,
    // 'tool' role messages are filtered out before mapping; treat everything
    // else as user/assistant/system (the package's MessageRole).
    role: isUser ? "user" : message.role === "system" ? "system" : "assistant",
    content: message.content,
    segments,
    attachments: message.attachments?.map((a) => ({
      id: a.id,
      name: a.name,
      size: a.size,
      type: a.type,
      isImage: a.isImage,
      dataUrl: a.dataUrl,
      // What the LB did with this file (tdd-0062) — drives the state badge.
      status: a.outcome
        ? {
            kind: a.outcome.state,
            detail: a.outcome.detail,
            // Offered only for indexed files: it is the one state where the
            // full content exists but deliberately wasn't sent. Inlined files
            // are already whole, and failed/truncated ones have nothing more
            // to give.
            onIncludeFull:
              a.outcome.state === "indexed" && opts.onIncludeFullFile
                ? () =>
                    opts.onIncludeFullFile?.({
                      name: a.name,
                      path: a.outcome?.path,
                    })
                : undefined,
          }
        : undefined,
      // Edit provenance (tdd-0076 Phase 4). Only for a derived artifact — an
      // original is its own root and has nothing to say here.
      lineage:
        a.derivedFrom && a.rootKey && a.rootKey !== a.storageKey
          ? {
              editPrompt: a.editPrompt,
              step: a.editStep,
              onRevertToOriginal: opts.onRevertToOriginal
                ? () =>
                    opts.onRevertToOriginal?.({
                      rootKey: a.rootKey!,
                      name: a.name,
                    })
                : undefined,
            }
          : undefined,
      // Durable storage state (tdd-0076). Separate from `status` because the
      // two are orthogonal — a file can be indexed for the model AND stored.
      storage: a.storageKey
        ? {
            stored: true,
            onDelete: opts.onDeleteArtifact
              ? () =>
                  opts.onDeleteArtifact?.({
                    messageId: message.id,
                    attachmentId: a.id,
                    storageKey: a.storageKey!,
                  })
              : undefined,
            deleting: opts.deletingArtifacts?.has(a.storageKey),
          }
        : undefined,
    })),
    createdAt: message.createdAt,
    authorName: isUser ? undefined : getModelDisplayName(effectiveModelId),
    isPreview: message.isPreview,
    systemNote: message.systemNote,
    extras,
  };
}

// ── Registry plugins ────────────────────────────────────────────────────────

const thinkingRenderer: SegmentRenderer = ({ segment }) => {
  const data = segment.data as ThinkingSegmentData | undefined;
  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={data?.defaultExpanded ? "thinking" : undefined}
      className="border rounded-md bg-muted/30"
    >
      <AccordionItem value="thinking" className="border-0">
        <AccordionTrigger className="px-3 py-2 text-sm hover:no-underline">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Brain className="h-4 w-4" />
            <span>Thinking</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-3 pb-3">
          <div className="text-muted-foreground">
            <StreamingMarkdown content={segment.text ?? ""} size="sm" />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const toolCallRenderer: SegmentRenderer = ({ segment }) => {
  const data = segment.data as ToolCallSegmentData | undefined;
  if (!data?.toolCalls?.length) return null;
  return <ToolCalls toolCalls={data.toolCalls} />;
};

const memoryExtrasRenderer: ExtrasRenderer = ({ message, isOwn }) => {
  const extras = message.extras as ChatExtras | undefined;
  if (!extras) return null;
  const {
    memorySaved,
    recalledMemories,
    recalledQuery,
    onDeleteMemory,
    brainBaseUrl,
    locationConsent,
    failed,
  } = extras;
  return (
    <>
      {failed && (
        // Aligned to the same edge as the user's own message. Destructive
        // colouring on the text but not the button: the failure is the alarming
        // part, the retry is the reassuring one, and making both red reads as
        // "this too has gone wrong".
        <div
          className={cn(
            "flex items-center gap-2 text-xs",
            isOwn && "justify-end",
          )}
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
          <span className="text-destructive">{failed.message}</span>
          {failed.onRetry && (
            <button
              type="button"
              onClick={failed.onRetry}
              className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3" aria-hidden="true" />
              Retry
            </button>
          )}
        </div>
      )}
      {locationConsent && (
        <LocationConsent
          toolName={locationConsent.toolName}
          reason={locationConsent.reason}
          status={locationConsent.status}
          error={locationConsent.error}
          onShare={locationConsent.onShare}
          onDecline={locationConsent.onDecline}
        />
      )}
      {memorySaved && (
        // End-aligned at every breakpoint, matching the user bubble's avatar,
        // border and text (which no longer flip back to the left at `md:`).
        <div className={cn(isOwn && "flex justify-end")}>
          <MemorySaved
            fact={memorySaved.fact}
            onDelete={
              memorySaved.id && onDeleteMemory
                ? () => onDeleteMemory(message.id)
                : undefined
            }
          />
        </div>
      )}
      {recalledMemories && recalledMemories.length > 0 && (
        <RecalledMemories
          memories={recalledMemories}
          query={recalledQuery}
          brainBaseUrl={brainBaseUrl}
        />
      )}
    </>
  );
};

/**
 * Generative-UI segment (tdd-0059). Delegates to whatever renderer the host
 * registered; renders a placeholder when there is none, so this is safe to
 * install unconditionally.
 */
const componentRenderer: SegmentRenderer = ({ segment }) => {
  const data = segment.data as ComponentSegmentData | undefined;
  return (
    <ComponentSegment jsonl={segment.text ?? ""} partial={data?.partial ?? false} />
  );
};

/** The renderer set apps/chat installs into <MessageRenderersProvider>. */
export const chatRenderers: MessageRenderers = {
  segments: {
    thinking: thinkingRenderer,
    "tool-call": toolCallRenderer,
    component: componentRenderer,
  },
  extras: memoryExtrasRenderer,
};
